import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useMenu } from '@context/MenuContext'

import { BreadcrumbHomeIcon } from '@icons/BreadcrumbHomeIcon'

import { Body2 } from '@ui/Typography'

import { useWindowResize } from '@utils/domUtils'
import { isNoTabsPath, isSignUpPath } from '@utils/path'

import { Button } from '@global/button/Button'
import IconButton from '@global/iconButton/IconButton'
import { MenuSection } from '@global/nav/menuSections/useMenuSections'

import styles from './Breadcrumbs.module.scss'

interface BreadcrumbItem {
  id: string
  label: string
  path: string
}

export const Breadcrumbs: React.FC = () => {
  const location = useLocation()
  const { menuItems } = useMenu()
  const isMobile = useWindowResize()

  const breadcrumbs = useMemo(() => {
    let currentPath = location.pathname

    const langMatch = currentPath.match(/^\/([a-z]{2})\/(.*)$/)
    if (langMatch) {
      currentPath = '/' + langMatch[2]
    }

    const result: BreadcrumbItem[] = [
      {
        id: 'home',
        label: 'Home',
        path: '/dashboard',
      },
    ]

    const findPath = (
      items: MenuSection[],
      path: string,
      currentBreadcrumbs: BreadcrumbItem[] = []
    ): BreadcrumbItem[] | null => {
      for (const item of items) {
        if (item.condition === false) {
          continue
        }

        const currentItem = item.path
          ? {
              id: item.id,
              label: item.label,
              path: item.path,
            }
          : null

        const newBreadcrumbs = currentItem
          ? [...currentBreadcrumbs, currentItem]
          : currentBreadcrumbs

        if (item.path === path) {
          return newBreadcrumbs
        }

        if (item.path && path.startsWith(item.path)) {
          return [...newBreadcrumbs]
        }

        if (item.children && !item.path) {
          const parentItem = {
            id: item.id,
            label: item.label,
            path: item.children.find((child) => child.path)?.path || '/dashboard',
          }
          const newBreadcrumbs = [...currentBreadcrumbs, parentItem]

          const childResult = findPath(item.children, path, newBreadcrumbs)
          if (childResult) {
            return childResult
          }
        }
      }

      return null
    }

    const pathBreadcrumbs = findPath(menuItems, currentPath)

    if (pathBreadcrumbs) {
      result.push(...pathBreadcrumbs)
    }

    return result
  }, [location.pathname, menuItems])

  if (isSignUpPath(location.pathname) || isNoTabsPath(location.pathname)) {
    return null
  }

  if (isMobile) {
    return <div className={styles.breadcrumbsPlaceholder} />
  }

  if (breadcrumbs.length <= 1) {
    return <div className={styles.breadcrumbSpacer} />
  }

  return (
    <Body2 className={styles.breadcrumbs}>
      {breadcrumbs.map((crumb, index) => (
        <Breadcrumb breadcrumbs={breadcrumbs} breadcrumb={crumb} index={index} />
      ))}
    </Body2>
  )
}

interface BreadcrumbItems {
  breadcrumbs: BreadcrumbItem[]
  breadcrumb: BreadcrumbItem
  index: number
}

const Breadcrumb: React.FC<BreadcrumbItems> = (props) => {
  const { breadcrumbs, index, breadcrumb: crumb } = props

  const isLast = index === breadcrumbs.length - 1

  if (index === 0) {
    return <BreadcrumbHome {...crumb} />
  }

  if (isLast || index === 1) {
    return <BreadcrumbText {...crumb} />
  }

  return <BreadcrumbLink {...crumb} />
}

const BreadcrumbHome: React.FC<BreadcrumbItem> = (crumb) => {
  const navigate = useNavigate()

  return (
    <IconButton onClick={() => navigate(crumb.path)} className={styles.home}>
      <BreadcrumbHomeIcon />
    </IconButton>
  )
}

const BreadcrumbLink: React.FC<BreadcrumbItem> = (crumb) => {
  const navigate = useNavigate()

  return (
    <>
      <span>{'/'}</span>
      <Button appearance='link' onClick={() => navigate(crumb.path)} className={styles.button}>
        {crumb.label}
      </Button>
    </>
  )
}

const BreadcrumbText: React.FC<BreadcrumbItem> = (crumb) => {
  return (
    <>
      <span>{'/'}</span>
      <span>{crumb.label}</span>
    </>
  )
}
